import React, {ForwardedRef, forwardRef, ReactElement, useCallback, useEffect, useState} from "react";
import {Box, FormControl, FormLabel, MenuItem, Select, SelectChangeEvent, Typography} from "@mui/material";
import {useController} from "react-hook-form";
import {FieldPath, FieldValues} from "react-hook-form/dist/types";
import {SelectInputProps} from "../../common/types/selectInputProps.type";
import {OptionValue} from "../../common/types/optionValue.type";
import {Option} from "../../common/types/option.type";

const SelectInput = forwardRef(<T extends FieldValues, TName extends FieldPath<T>, TItem>(
    props: SelectInputProps<T, TName, TItem>,
    ref: ForwardedRef<HTMLSelectElement>
): ReactElement => {

    const {
        label,
        hint,
        errorMessage,
        error,
        items,
        disabled,
        placeholder,
        hidePlaceholderMenuItem,
        startAdornment,
        labelMapper,
        getOptionValue,
        getOptionLabel,
        fieldMapper,
        valueMapper,
        ...rest
    } = props;

    const {field} = useController({
        ...rest
    });

    const {onChange, value, ref: inputRef, ...otherField} = field;

    const [innerValue, setInnerValue] = useState<OptionValue>("");
    const [options, setOptions] = useState<Option[]>([]);
    const [hidePlaceholder, setHidePlaceholder] = useState(false);

    useEffect(() => {
        if (!value) {
            setInnerValue(placeholder ?? "")
        } else {
            setInnerValue(valueMapper(value, items))
        }
        setOptions(items.map(i => ({label: getOptionLabel(i), value: getOptionValue(i)})));

    }, [value, items, getOptionLabel, getOptionValue, valueMapper, placeholder])

    const onChanged = useCallback((event: SelectChangeEvent<OptionValue>) => {
        onChange(fieldMapper(event.target.value, items));
    }, [fieldMapper, items, onChange]);

    const combineRefs = useCallback((node: HTMLSelectElement | null) => {
        // Handle the ref from useController (inputRef)
        if (typeof inputRef === 'function') {
            inputRef(node);
        } else if (inputRef) {
            (inputRef as React.MutableRefObject<HTMLSelectElement | null>).current = node;
        }

        // Handle the forwarded ref from the parent component
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            (ref as React.MutableRefObject<HTMLSelectElement | null>).current = node;
        }
    }, [inputRef, ref]);

    return (
        <Box>
            <FormControl error={error} fullWidth>
                {label && (
                    <FormLabel id={`${field.name}-label`} className={"input-label"} sx={{mb: 0.5}}>
                        {label}
                    </FormLabel>
                )}
                <Select
                    ref={combineRefs}
                    id={field.name}
                    labelId={`${field.name}-label`}
                    className={"input-field"}
                    {...otherField}
                    disabled={disabled}
                    multiple={false}
                    value={innerValue}
                    onChange={onChanged}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                mt: "2px",
                                maxHeight: 260
                            },
                        },
                    }}
                    startAdornment={
                        startAdornment && (
                            <Box display="flex" justifyContent="center" mr={1} color={disabled ? '#123123' : '#000000'}>
                                {startAdornment}
                            </Box>
                        )
                    }
                    renderValue={placeholder && labelMapper ? (selected) => {
                        if (selected === placeholder) {
                            return <Typography component="span" variant={"body2"}>{placeholder}</Typography>
                        }
                        return <Typography component="span" variant={"body2"}>{labelMapper(selected, items)}</Typography>;
                    } : undefined}
                >
                    {placeholder ? <MenuItem style={ hidePlaceholderMenuItem ? { display: "none"} : undefined } value={placeholder}>{placeholder}</MenuItem> : null}
                    {options.map((option, index) => (
                        <MenuItem key={index} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
                {hint &&
                    <Typography component="div" variant={"body2"} className={"input-help"}>
                        {hint}
                    </Typography>
                }
                {errorMessage &&
                    <Typography component="div" variant={"body2"} className={"error-message"}>
                        {errorMessage}
                    </Typography>
                }
            </FormControl>
        </Box>
    );
});

export {SelectInput}
